
import React, { useState, useRef } from 'react';
import { motion, PanInfo } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void>;
  refreshThreshold?: number;
  className?: string;
}

const PullToRefresh = ({ 
  children, 
  onRefresh, 
  refreshThreshold = 80,
  className 
}: PullToRefreshProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDrag = (event: any, info: PanInfo) => {
    if (window.scrollY > 0) return;
    
    const distance = Math.max(0, info.offset.y);
    setPullDistance(distance);
  };

  const handleDragEnd = async (event: any, info: PanInfo) => {
    if (info.offset.y > refreshThreshold && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  };

  const refreshProgress = Math.min(pullDistance / refreshThreshold, 1);

  return (
    <div ref={containerRef} className={cn('relative overflow-hidden', className)}>
      {/* Pull to refresh indicator */}
      <motion.div
        className="absolute top-0 left-0 right-0 flex items-center justify-center bg-background/95 backdrop-blur-sm border-b z-50"
        initial={{ height: 0, opacity: 0 }}
        animate={{ 
          height: pullDistance > 0 ? Math.min(pullDistance, 80) : 0,
          opacity: pullDistance > 0 ? 1 : 0
        }}
        transition={{ type: 'tween', duration: 0.1 }}
      >
        <motion.div
          animate={{ rotate: isRefreshing ? 360 : refreshProgress * 360 }}
          transition={{ 
            duration: isRefreshing ? 1 : 0,
            repeat: isRefreshing ? Infinity : 0,
            ease: 'linear'
          }}
        >
          <RefreshCw 
            className={cn(
              'h-6 w-6 transition-colors',
              refreshProgress >= 1 ? 'text-primary' : 'text-muted-foreground'
            )}
          />
        </motion.div>
        <span className="ml-2 text-sm text-muted-foreground">
          {isRefreshing ? 'Refreshing...' : refreshProgress >= 1 ? 'Release to refresh' : 'Pull to refresh'}
        </span>
      </motion.div>

      {/* Main content */}
      <motion.div
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.2}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        style={{ y: pullDistance > 0 ? pullDistance : 0 }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export default PullToRefresh;
